import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fmtDate, fmtHrs, sailingPct, qualifiesSeaDay, seaDaysByPerson, engineHours, hullNm, totalHrs, isStayEntry, nightsAboard, isRaceEntry, voyageTrack, lastUnderway, isOutToday, sinceParts } from '../lib/utils';
import { FUEL_RANGE_CURVE } from '../lib/constants';
import { VoyageList, VoyagePage } from './VoyagesTab';

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 900);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 900);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

function tripLabel(e) {
  if (e.race)                        return { label:'RACE',          accent:'#C0392B' };
  if (e.trip_type==='Passage')       return { label:'PASSAGE',       accent:'#1A6B2E' };
  if (e.trip_type==='Delivery')      return { label:'DELIVERY',      accent:'#7A5C00' };
  if (e.trip_type==='Training')      return { label:'TRAINING',      accent:'#6A7D85' };
  if (e.trip_type==='At Dock')       return { label:'AT DOCK',       accent:'#4A6B8A' };
  if (e.trip_type==='At Anchor' || e.trip_type==='Day at Anchor') return { label:'AT ANCHOR', accent:'#4A6B8A' };
  return                                    { label:'DAY SAIL',      accent:'var(--gold-deep, #8B6508)' };
}

const SANS = "'Plus Jakarta Sans',sans-serif";
const MONO = "'IBM Plex Mono',monospace";

// ── Small uppercase mono caption, used on map chips ──
const T = {
  meta: { fontFamily:MONO, fontWeight:700, fontSize:9, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--slate)' },
};

const routeOf = (e) => {
  const dep = e.departure || 'EBM';
  if (isStayEntry(e)) return e.dest_name || e.destination || dep;   // stays have one location
  const dest = e.destination || e.arrival || dep;
  return dep === dest ? `${dep} (round trip)` : `${dep} → ${dest}`;
};

const crewNames = (e) => [
  e.captain || 'Frederick',
  ...(e.crew ? e.crew.split(',').map(s=>s.trim()).filter(Boolean) : []),
  ...(e.guests ? e.guests.split(',').map(s=>s.trim()).filter(Boolean) : []),
];

// Arrival date/time as a sortable timestamp: entry date + arrive_time (falls
// back to depart_time, then noon). Used to order the feed by most recent arrival.
const arrivalTs = (e) => {
  const base = new Date((e.date || '1970-01-01') + 'T12:00:00').getTime();
  const hm = /^(\d{1,2}):(\d{2})/.exec(e.arrive_time || e.depart_time || '');
  if (!hm) return isNaN(base) ? 0 : base;
  const t = new Date(`${e.date}T${String(+hm[1]).padStart(2,'0')}:${hm[2]}:00`).getTime();
  return isNaN(t) ? (isNaN(base) ? 0 : base) : t;
};

// Timeline position for the feed. A passage happens at its arrival moment.
// A stay spans arrival → departure, so it belongs just AFTER the passage that
// brought you in (same arrival time) and BEFORE the next passage that departs.
// We key stays on their arrival, then a tie-break nudges a stay to sort just
// later than a passage sharing that timestamp (so the stay sits above it in the
// newest-first feed).
const timelineTs = (e) => {
  const ts = arrivalTs(e);
  // +1 minute for stays so a stay edges ahead of its arriving passage on ties.
  return isStayEntry(e) ? ts + 60000 : ts;
};

// ── A3 summary card — media left, description inline, two data rows + crew ──
// ── Glyph for a stay: anchor, or a dock cleat ──
function StayGlyph({ type }) {
  const dock = type === 'At Dock';
  return (
    <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--slate)', opacity:.55 }}>
      {dock ? (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 20h18"/><path d="M6 20V9"/><path d="M18 20V9"/><path d="M3 9h18"/><path d="M9 9V5"/><path d="M15 9V5"/></svg>
      ) : (
        <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="5" r="2"/><line x1="12" y1="7" x2="12" y2="21"/><line x1="7" y1="11" x2="17" y2="11"/><path d="M4 15a8 8 0 0 0 16 0"/></svg>
      )}
    </div>
  );
}

// ── Tiny route sparkline drawn from the stored track ──
// Initials from the arrival (or departure) place, for a monogram tile when
// there's no photo and no GPS track — e.g. imported historical entries.
function tileInitials(entry) {
  const s = (entry.destination || entry.arrival || entry.dest_name || entry.departure || '').trim();
  if (!s) return '·';
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    const w = words[0];
    return (w.length <= 3 ? w : w.slice(0, 2)).toUpperCase();
  }
  return words.slice(0, 2).map(w => w[0]).join('').toUpperCase();
}
function Monogram({ entry, race }) {
  const txt = tileInitials(entry);
  const nm = Number(entry.nm);
  // Data line: distance if logged, else the date, so the tile is never empty.
  let data = null;
  if (!isNaN(nm) && nm > 0) data = `${nm % 1 === 0 ? nm : nm.toFixed(1)} nm`;
  else if (entry.date) { const d = new Date(entry.date + 'T00:00:00'); if (!isNaN(d)) data = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  const ink = race ? 'var(--red)' : 'var(--navy)';
  return (
    <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'12px 6px', background:'var(--tint, #FAF8F4)' }}>
      <span style={{ fontFamily:MONO, fontSize:23, fontWeight:600, color:ink, lineHeight:1 }}>{txt}</span>
      {data && <span style={{ width:26, height:1, background:'var(--border, #D5CCBA)', margin:'7px 0' }} />}
      {data && <span style={{ fontFamily:MONO, fontSize:12, fontWeight:600, color:ink, lineHeight:1 }}>{data}</span>}
    </div>
  );
}

function Sparkline({ track, accent }) {
  const rp = trackPath(track, 96, 96, 14);
  if (!rp) return <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--slate)', opacity:.4 }}>
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 17l9-13 9 13"/><path d="M3 17c0 2.5 18 2.5 18 0"/></svg>
  </div>;
  return (
    <svg viewBox="0 0 96 96" style={{ width:'100%', height:'100%' }}>
      <path d={rp.d} fill="none" stroke="#6E8799" strokeOpacity="0.65" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round"/>
      <circle cx={rp.sx} cy={rp.sy} r="3" fill="#B8860B"/>
      <circle cx={rp.ex} cy={rp.ey} r="3" fill={accent}/>
    </svg>
  );
}

function SummaryCard({ entry, active, onOpen, first, compact = false, allBoats = false, boatsById = {} }) {
  const isRace = isRaceEntry(entry);
  const stay = isStayEntry(entry);
  const typeLabel = isRace ? 'Race'
    : entry.trip_type === 'At Dock' ? 'At Dock'
    : (entry.trip_type === 'At Anchor' || entry.trip_type === 'Day at Anchor') ? 'At Anchor'
    : entry.trip_type || 'Day Sail';
  const crew = crewNames(entry);
  const sailP = sailingPct(entry);
  const moving = fmtHrs(entry.moving_hrs);
  const qualifies = qualifiesSeaDay(entry);
  const title = entry.title || routeOf(entry);

  // One lead number, two quiet supports. Everything else lives in the detail view.
  const supports = stay
    ? [entry.dest_type, fmtHrs(totalHrs(entry)) && `${fmtHrs(totalHrs(entry))} aboard`].filter(Boolean).join(' · ')
    : [moving && `${moving} moving`, sailP != null && `${sailP}% sail`].filter(Boolean).join(' · ');
  // Compact list rows fold the crew into the meta line instead of a second row.
  const metaLine = compact
    ? [supports, crew.length ? crew.join(', ') : null].filter(Boolean).join(' · ')
    : supports;

  return (
    <div onClick={()=>onOpen(entry)} style={{
      display:'flex', cursor:'pointer', alignItems:'stretch', minHeight: compact ? 96 : 118,
      background: active ? 'var(--tint, #FAF8F4)' : 'transparent',
      borderTop: first ? 'none' : '1px solid var(--hair, #E7E1D5)',
    }}>
      {/* Media fills its side of the tile, edge to edge, full row height */}
      <div style={{ flex: compact ? '0 0 92px' : '0 0 116px', alignSelf:'stretch', overflow:'hidden', background:'var(--tint, #FAF8F4)', display:'flex', alignItems:'center', justifyContent:'center' }}>
        {entry.photo_url
          ? <img src={entry.photo_url} alt="" loading="lazy" decoding="async" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
          : stay
            ? <StayGlyph type={entry.trip_type}/>
            : (Array.isArray(entry.track) && entry.track.length > 1)
              ? <Sparkline track={entry.track} accent={isRace ? '#C0392B' : '#6E8799'}/>
              : <Monogram entry={entry} race={isRace}/>}
      </div>

      <div style={{ flex:1, minWidth:0, padding: compact ? '12px 14px' : '14px 16px' }}>
        <div style={{ fontFamily:MONO, fontSize:9, fontWeight:600, letterSpacing:'.1em', textTransform:'uppercase', color: isRace ? 'var(--red)' : 'var(--slate)' }}>
          {(fmtDate(entry.date)||'').replace(/^\w+,\s*/,'')} · {typeLabel}
          {qualifies && <span title="Qualifying day at sea" style={{ display:'inline-block', width:5, height:5, borderRadius:'50%', background:'var(--gold)', marginLeft:6, verticalAlign:'middle' }}/>}
          {allBoats && boatsById[entry.boat_id] && (
            <span style={{ display:'inline-block', fontFamily:SANS, fontSize:8.5, fontWeight:700, letterSpacing:'.03em', color:'#fff', background: boatsById[entry.boat_id].color, padding:'2px 7px', borderRadius:5, marginLeft:8, verticalAlign:'middle', textTransform:'none' }}>{boatsById[entry.boat_id].name}</span>
          )}
        </div>

        <div style={{ fontFamily:SANS, fontSize: compact ? 15 : 16, fontWeight:600, color:'var(--navy)', letterSpacing:'-.01em', margin:'4px 0 5px', lineHeight:1.25, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{title}</div>

        <div style={{ display:'flex', alignItems:'baseline', flexWrap:'nowrap', gap:8, minWidth:0 }}>
          {stay ? (
            <span style={{ fontFamily:MONO, fontSize:19, fontWeight:600, color:'var(--navy)', letterSpacing:'-.01em', whiteSpace:'nowrap', flexShrink:0 }}>
              {nightsAboard(entry) > 0 ? nightsAboard(entry) : '—'}<span style={{ fontSize:11, color:'var(--slate)', fontWeight:400 }}>&nbsp;{nightsAboard(entry) === 1 ? 'night' : 'nights'}</span>
            </span>
          ) : (entry.nm != null && entry.nm !== '' && (
            <span style={{ fontFamily:MONO, fontSize:19, fontWeight:600, color:'var(--navy)', letterSpacing:'-.01em', whiteSpace:'nowrap', flexShrink:0 }}>
              {entry.nm}<span style={{ fontSize:11, color:'var(--slate)', fontWeight:400 }}>&nbsp;nm</span>
            </span>
          ))}
          {metaLine && <span style={{ fontFamily:MONO, fontSize:11.5, color:'var(--slate)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', minWidth:0 }}>{metaLine}</span>}
        </div>

        {!compact && crew.length > 0 && (
          <div style={{ fontFamily:SANS, fontSize:11.5, color:'var(--slate)', marginTop:6, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{crew.join(', ')}</div>
        )}
      </div>
    </div>
  );
}


// ── Estimated fuel burned this passage, from engine hours × the Yanmar curve ──
function passageFuel(entry) {
  const engDelta = (entry.engine_end && entry.engine_start) ? (Number(entry.engine_end) - Number(entry.engine_start)) : null;
  if (entry.fuel_est != null && entry.fuel_est !== '') return { gal: Number(entry.fuel_est), hrs: engDelta, measured: true };
  if (engDelta == null || !(engDelta > 0)) return null;
  const rpm = Number(entry.avg_rpm) || 2200;
  const rows = FUEL_RANGE_CURVE;
  let gph = rows[0].gph;
  for (let i = 0; i < rows.length; i++) {
    if (rpm <= rows[i].rpm) {
      if (i === 0) { gph = rows[0].gph; break; }
      const a = rows[i - 1], b = rows[i];
      gph = a.gph + (b.gph - a.gph) * ((rpm - a.rpm) / (b.rpm - a.rpm));
      break;
    }
    gph = rows[i].gph;
  }
  return { gal: Math.round(engDelta * gph * 10) / 10, hrs: engDelta, gph: Math.round(gph * 100) / 100, measured: false };
}

const Row = ({ k, children }) => (
  <div className="pmf-row" style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'9px 0' }}>
    <span style={{ fontFamily:SANS, fontSize:14, fontWeight:400, color:'var(--slate)' }}>{k}</span>
    <span style={{ fontFamily:MONO, fontSize:14, fontWeight:500, color:'var(--navy)' }}>{children}</span>
  </div>
);
const U = ({ children }) => <span style={{ fontFamily:MONO, color:'var(--slate)', fontWeight:400, fontSize:11 }}>{children}</span>;
const Hl = ({ children }) => <span style={{ color:'var(--gold)' }}>{children}</span>;
const Sec = ({ children }) => <div style={{ padding:'24px 22px 0' }}><h2 style={{ fontFamily:SANS, fontSize:11, fontWeight:600, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--slate)', margin:0 }}>{children}</h2></div>;
const Rows = ({ children }) => <div style={{ padding:'12px 22px 4px' }}>{children}</div>;
const Hair = () => <div style={{ height:1, background:'var(--hair, #E7E1D5)', margin:'0 22px' }}/>;

function DetailView({ entry, entries = [], fuelLog = [], onEdit, onDelete, onBack, wide = false }) {
  const [showAll, setShowAll] = useState(false);
  if (!entry) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', color:'var(--slate)', fontSize:14 }}>Select a log entry</div>
  );
  const { label } = tripLabel(entry);
  const crew = crewNames(entry);
  const stay = isStayEntry(entry);
  const nights = nightsAboard(entry);
  const sailP = sailingPct(entry);
  const engDelta = (entry.engine_end && entry.engine_start) ? (Number(entry.engine_end)-Number(entry.engine_start)) : null;
  const genDelta = (entry.gen_end && entry.gen_start) ? (Number(entry.gen_end)-Number(entry.gen_start)) : null;
  const since = fuelSinceFill(entry, fuelLog);
  const burn = passageFuel(entry);
  const elapsed = entry.total_time || fmtHrs(entry.time_at_sea);
  const moving = fmtHrs(entry.moving_hrs);
  const curGain = (entry.avg_sog && entry.avg_stw) ? Math.round((Number(entry.avg_sog)-Number(entry.avg_stw))*10)/10 : null;
  const qualifies = qualifiesSeaDay(entry);
  const seaDays = seaDaysByPerson(entries);
  const daysFor = (n) => seaDays.find(p => p.person === n)?.days ?? 0;
  const coord = (lat, lon) => (lat != null && lat !== '' && lon != null && lon !== '')
    ? `${Math.abs(Number(lat)).toFixed(3)}\u00B0${Number(lat)>=0?'N':'S'} ${Math.abs(Number(lon)).toFixed(3)}\u00B0${Number(lon)>=0?'E':'W'}` : null;
  const startCoord = coord(entry.start_lat, entry.start_lon);
  const endCoord = coord(entry.end_lat, entry.end_lon);
  const sailPlan = [entry.sail_main, entry.sail_headsail, entry.sail_spin].filter(Boolean).join(', ');
  const hasWx = entry.avg_wind || entry.max_wind || entry.avg_twd || entry.avg_current || sailPlan || entry.waves;
  const hasPwr = engDelta != null || genDelta != null || burn || since || (entry.engine_end != null && entry.engine_end !== '') || (entry.gen_end != null && entry.gen_end !== '');
  const hasMore = entry.avg_stw || curGain != null || entry.avg_polar || entry.twd_stddev || entry.batt_start || elapsed || entry.stationary_hrs;
  const stars = Number(entry.stars) || 0;

  return (
    <div style={{ background:'var(--surface)' }}>
      {/* Header — quiet eyebrow, no wordmark; qualifying mark sits top right */}
      <div style={{ position:'relative', padding: wide ? '30px 32px 24px' : '22px 22px 18px' }}>
        {onBack && <div onClick={onBack} style={{ position:'absolute', top:20, left:22, fontFamily:MONO, fontSize:12, fontWeight:600, color:'var(--slate)', cursor:'pointer' }}>‹ Log</div>}
        {qualifies && (
          <div title="Qualifying day at sea (3h+ elapsed)" style={{ position:'absolute', top:18, right:20, display:'inline-flex', alignItems:'center', gap:6, border:'1px solid var(--gold)', color:'var(--gold)', borderRadius:20, padding:'4px 10px', fontFamily:SANS, fontSize:10.5, fontWeight:600 }}>
            <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="5" r="2"/><line x1="12" y1="7" x2="12" y2="21"/><line x1="7" y1="11" x2="17" y2="11"/><path d="M4 15a8 8 0 0 0 16 0"/></svg>
            Qualifying day
          </div>
        )}
        <div style={{ display:'flex', alignItems:'center', gap:7, marginTop: onBack ? 24 : 0, fontFamily:MONO, fontSize:9, fontWeight:600, letterSpacing:'.12em', textTransform:'uppercase', color:'var(--slate)' }}>
          <span style={{ width:5, height:5, borderRadius:'50%', background:'var(--gold)' }}/> Passage Report
        </div>
        <h1 style={{ fontFamily:SANS, fontSize: wide ? 34 : 25, fontWeight:700, letterSpacing:'-.02em', lineHeight:1.15, color:'var(--navy)', margin:'12px 0 6px' }}>{entry.title || routeOf(entry)}</h1>
        <div style={{ fontFamily:SANS, fontSize:14, color:'var(--slate)' }}>{fmtDate(entry.date)} · {label}</div>
      </div>

      {/* Media, full bleed */}
      <MediaCarousel track={entry.track} nm={entry.nm} photos={entry.photos} coverUrl={entry.photo_url} wide={wide}/>


      {/* Location: one place for a stay, From → To for a passage */}
      {stay ? (
        (entry.destination || entry.dest_name || entry.departure) && (
          <div style={{ padding:'22px 22px 4px' }}>
            <div style={{ fontFamily:SANS, fontSize:10, fontWeight:600, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--slate)' }}>Location</div>
            <div style={{ fontFamily:SANS, fontSize:17, fontWeight:600, color:'var(--navy)', marginTop:4, letterSpacing:'-.01em' }}>{routeOf(entry)}</div>
            <div style={{ fontFamily:MONO, fontSize:11, color:'var(--slate)', marginTop:3 }}>{[entry.dest_type, startCoord].filter(Boolean).join(' · ')}</div>
          </div>
        )
      ) : (entry.departure || entry.destination || startCoord) && (
        <div style={{ display:'flex', alignItems:'center', gap:14, padding: wide ? '26px 32px 4px' : '22px 22px 4px' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontFamily:SANS, fontSize:10, fontWeight:600, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--slate)' }}>From</div>
            <div style={{ fontFamily:SANS, fontSize:17, fontWeight:600, color:'var(--navy)', marginTop:4, letterSpacing:'-.01em' }}>{entry.departure || '—'}</div>
            <div style={{ fontFamily:MONO, fontSize:11, color:'var(--slate)', marginTop:3 }}>{[entry.depart_time, startCoord].filter(Boolean).join(' · ')}</div>
          </div>
          <div style={{ flex: wide ? '0 0 140px' : '0 0 40px', height:1, background:'var(--border)', position:'relative' }}>
            <span style={{ position:'absolute', right:-1, top:-2.5, width:6, height:6, borderRadius:'50%', background:'var(--gold)' }}/>
          </div>
          <div style={{ flex:1, minWidth:0, textAlign:'right' }}>
            <div style={{ fontFamily:SANS, fontSize:10, fontWeight:600, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--slate)' }}>To</div>
            <div style={{ fontFamily:SANS, fontSize:17, fontWeight:600, color:'var(--navy)', marginTop:4, letterSpacing:'-.01em' }}>{entry.destination || entry.arrival || '—'}</div>
            <div style={{ fontFamily:MONO, fontSize:11, color:'var(--slate)', marginTop:3 }}>{[entry.arrive_time, endCoord].filter(Boolean).join(' · ')}</div>
          </div>
        </div>
      )}
      {entry.stopover && !isStayEntry(entry) && (
        <div style={{ fontFamily:MONO, fontSize:11, color:'var(--slate)', padding:'0 22px 4px', textAlign:'center' }}>via {entry.stopover}</div>
      )}

      {/* Hero stats */}
      {stay ? (
        <>
          <div style={{ display:'flex', alignItems:'baseline', gap:8, padding:'26px 22px 4px' }}>
            <div>
              <span style={{ fontFamily:MONO, fontSize:44, fontWeight:600, letterSpacing:'-.02em', color:'var(--navy)', lineHeight:1 }}>{nights > 0 ? nights : '—'}</span>
              <span style={{ fontFamily:MONO, fontSize:14, color:'var(--slate)' }}> {nights === 1 ? 'night' : 'nights'} aboard</span>
            </div>
          </div>
          <div style={{ display:'flex', padding:'18px 22px 22px', gap:26 }}>
            {[[fmtHrs(totalHrs(entry))||'—','Time aboard'],[entry.dest_type||'—','Berth'],[entry.max_wind ?? '—','Max Wind']].map(([v,k])=>(
              <div key={k} style={{ flex:1 }}>
                <div style={{ fontFamily:MONO, fontSize:19, fontWeight:600, color:'var(--navy)' }}>{v}</div>
                <div style={{ fontFamily:SANS, fontSize:10, fontWeight:600, letterSpacing:'.09em', textTransform:'uppercase', color:'var(--slate)', marginTop:4 }}>{k}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div style={{ display:'flex', alignItems:'baseline', gap:8, padding:'26px 22px 4px' }}>
            <div>
              <span style={{ fontFamily:MONO, fontSize:44, fontWeight:600, letterSpacing:'-.02em', color:'var(--navy)', lineHeight:1 }}>{entry.nm ?? '—'}</span>
              <span style={{ fontFamily:MONO, fontSize:14, color:'var(--slate)' }}> nm</span>
            </div>
            {sailP != null && (
              <div style={{ marginLeft:'auto', textAlign:'right' }}>
                <div style={{ fontFamily:MONO, fontSize:30, fontWeight:600, color:'var(--gold)', lineHeight:1 }}>{sailP}%</div>
                <div style={{ fontFamily:SANS, fontSize:10, fontWeight:600, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--slate)', marginTop:4 }}>Sailing</div>
              </div>
            )}
          </div>
          <div style={{ display:'flex', padding:'18px 22px 22px', gap:26 }}>
            {[[moving||'—','Moving'],[entry.avg_sog ?? '—','Avg SOG'],[entry.max_sog ?? '—','Max SOG'],[entry.max_wind ?? '—','Max Wind']].map(([v,k])=>(
              <div key={k} style={{ flex:1 }}>
                <div style={{ fontFamily:MONO, fontSize:19, fontWeight:600, color:'var(--navy)' }}>{v}</div>
                <div style={{ fontFamily:SANS, fontSize:10, fontWeight:600, letterSpacing:'.09em', textTransform:'uppercase', color:'var(--slate)', marginTop:4 }}>{k}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Narrative, no box */}
      {entry.comments && (<><Hair/><div style={{ padding:'24px 22px', maxWidth:700, fontFamily:SANS, fontSize:15.5, lineHeight:1.8, color:'var(--ink)' }}>{entry.comments}</div></>)}
      <Hair/>

      {hasWx && (<>
        <Sec>Wind &amp; sea</Sec>
        <Rows>
          {(entry.avg_wind || entry.max_wind) && <Row k="Wind">{entry.avg_wind ?? '—'} <U>avg ·</U> {entry.max_wind ?? '—'} <U>max kt</U></Row>}
          {entry.avg_twd && <Row k="Direction">{entry.avg_twd}<U>°</U></Row>}
          {entry.avg_current && <Row k="Current">{entry.avg_current} <U>kt at</U> {entry.current_dir ?? '—'}<U>°</U></Row>}
          {entry.waves && <Row k="Sea state">{entry.waves}</Row>}
          {sailPlan && <Row k="Sail plan">{sailPlan}</Row>}
        </Rows>
      </>)}

      {hasPwr && (<>
        <Sec>Power &amp; fuel</Sec>
        <Rows>
          {engDelta != null
            ? <Row k="Engine">{Number(entry.engine_start).toLocaleString()} <U>→</U> {Number(entry.engine_end).toLocaleString()} <Hl>+{engDelta.toFixed(1)}</Hl></Row>
            : (entry.engine_end != null && entry.engine_end !== '') && <Row k="Engine">{Number(entry.engine_end).toLocaleString()} <U>h</U></Row>}
          {genDelta != null
            ? <Row k="Generator">{Number(entry.gen_start).toLocaleString()} <U>→</U> {Number(entry.gen_end).toLocaleString()} <Hl>+{genDelta.toFixed(1)}</Hl></Row>
            : (entry.gen_end != null && entry.gen_end !== '') && <Row k="Generator">{Number(entry.gen_end).toLocaleString()} <U>h</U></Row>}
          {burn && <Row k="Used this passage">{burn.measured ? '' : '~'}{burn.gal.toFixed(1)} <U>gal{burn.gph ? ` · ${burn.gph} gal/h` : ''}</U></Row>}
          {since && <Row k={`Since last fill (${(fmtDate(since.date)||'').replace(/^\w+,\s*/,'').replace(/,.*/,'')})`}>~{since.gal.toFixed(1)} <U>gal ·</U> {since.hrs}<U>h</U></Row>}
        </Rows>
      </>)}

      {hasMore && (<>
        <button onClick={()=>setShowAll(v=>!v)} style={{ margin:'18px 22px 0', width:'calc(100% - 44px)', background:'var(--tint, #FAF8F4)', border:'none', borderRadius:12, padding:13, fontFamily:SANS, fontSize:12, fontWeight:600, color:'var(--navy)', cursor:'pointer' }}>
          {showAll ? 'Fewer stats ↑' : 'All stats ↓'}
        </button>
        {showAll && (
          <Rows>
            {entry.avg_stw && <Row k="Avg STW">{entry.avg_stw} <U>kt</U></Row>}
            {curGain != null && <Row k="Current gain"><Hl>{curGain>0?'+':''}{curGain}</Hl> <U>kt</U></Row>}
            {(entry.avg_polar || entry.max_polar) && <Row k="Polar performance">{entry.avg_polar ?? '—'}<U>% avg ·</U> {entry.max_polar ?? '—'}<U>% max</U></Row>}
            {entry.twd_stddev && <Row k="Wind direction σ">{entry.twd_stddev}<U>°</U></Row>}
            {(entry.batt_start || entry.batt_end) && <Row k="Batteries">{entry.batt_start ?? '—'} <U>→</U> {entry.batt_end ?? '—'} <U>V{entry.batt_min?` · min ${entry.batt_min}`:''}</U></Row>}
            {elapsed && <Row k="Elapsed">{elapsed}</Row>}
            {entry.stationary_hrs && <Row k="Stationary">{fmtHrs(entry.stationary_hrs)}</Row>}
            {entry.avg_rpm && <Row k="Avg RPM">{Number(entry.avg_rpm).toLocaleString()}</Row>}
          </Rows>
        )}
      </>)}

      {(entry.wind || entry.temp != null || entry.visibility || entry.anchoring) && (<>
        <Sec>Weather &amp; conditions</Sec>
        <Rows>
          {entry.wind && <Row k="Wind">{entry.wind}</Row>}
          {entry.temp != null && entry.temp !== '' && <Row k="Temp">{Number(entry.temp)}<U>°F</U></Row>}
          {entry.visibility && <Row k="Visibility">{entry.visibility}</Row>}
          {entry.anchoring === 'O' && <Row k="Anchored">Overnight</Row>}
          {entry.anchoring === 'D' && <Row k="Anchored">Daytime</Row>}
        </Rows>
      </>)}

      {crew.length > 0 && (<>
        <Sec>Crew</Sec>
        <Rows>
          {crew.map((n,i)=>(
            <div key={n} className="pmf-row" style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', padding:'9px 0' }}>
              <span style={{ fontFamily:SANS, fontSize:14.5, color:'var(--ink)' }}>
                <span style={{ fontWeight: i===0?600:400, color: i===0?'var(--navy)':'var(--ink)' }}>{n}</span>
                {i===0 && <span style={{ fontSize:11, color:'var(--slate)' }}> · Skipper</span>}
              </span>
              <span style={{ fontFamily:MONO, fontSize:14, fontWeight:500, color:'var(--navy)' }}>{daysFor(n)} <U>days</U></span>
            </div>
          ))}
        </Rows>
      </>)}

      {(entry.difficulty || stars > 0) && (<>
        <Sec>Rating</Sec>
        <Rows>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 0' }}>
            <span style={{ fontFamily:SANS, fontSize:14, color:'var(--slate)' }}>{entry.difficulty || ''}</span>
            {stars > 0 && (
              <span style={{ display:'flex', gap:5 }}>
                {[1,2,3,4,5].map(i => <i key={i} style={{ width:8, height:8, borderRadius:'50%', background: i<=stars ? 'var(--gold)' : 'var(--hair, #E7E1D5)' }}/>)}
              </span>
            )}
          </div>
        </Rows>
      </>)}

      <div style={{ padding:22, display:'flex', justifyContent:'flex-end' }}>
        <span style={{ fontFamily:MONO, fontSize:10, color:'var(--slate)', letterSpacing:'.06em' }}>SO45 · 993</span>
      </div>

      <div style={{ display:'flex', gap:10, padding:'0 22px 24px' }}>
        <button onClick={()=>{ if(navigator.share){navigator.share({title:'PMF Passage Report', text:`${entry.title||routeOf(entry)} — ${entry.nm||''} nm`}).catch(()=>{}); } }} style={{ flex:1.6, background:'var(--navy)', color:'#fff', border:'none', borderRadius:12, padding:14, fontFamily:SANS, fontSize:12.5, fontWeight:600, cursor:'pointer' }}>Share</button>
        <button onClick={()=>onEdit(entry)} style={{ flex:1, background:'transparent', border:'none', borderRadius:12, padding:14, fontFamily:SANS, fontSize:12.5, fontWeight:600, color:'var(--slate)', cursor:'pointer' }}>Edit</button>
        <button onClick={()=>onDelete(entry)} style={{ flex:1, background:'transparent', border:'none', borderRadius:12, padding:14, fontFamily:SANS, fontSize:12.5, fontWeight:600, color:'var(--red)', cursor:'pointer' }}>Delete</button>
      </div>
    </div>
  );
}

// ── Route path from stored track (equirectangular, lat-corrected) ──
function trackPath(track, W, H, pad) {
  if (!Array.isArray(track) || track.length < 2) return null;
  const lats = track.map(p=>p.lat), lons = track.map(p=>p.lon);
  const minLa=Math.min(...lats), maxLa=Math.max(...lats), minLo=Math.min(...lons), maxLo=Math.max(...lons);
  const midLa=(minLa+maxLa)/2, kx=Math.cos(midLa*Math.PI/180);
  const spanX=(maxLo-minLo)*kx || 1e-6, spanY=(maxLa-minLa) || 1e-6;
  const sc=Math.min((W-2*pad)/spanX,(H-2*pad)/spanY);
  const ox=(W-spanX*sc)/2, oy=(H-spanY*sc)/2;
  const X=lo=>ox+(lo-minLo)*kx*sc, Y=la=>H-(oy+(la-minLa)*sc);
  const d=track.map((p,i)=>(i?'L':'M')+X(p.lon).toFixed(1)+' '+Y(p.lat).toFixed(1)).join(' ');
  const s=track[0], e=track[track.length-1];
  return { d, sx:+X(s.lon).toFixed(1), sy:+Y(s.lat).toFixed(1), ex:+X(e.lon).toFixed(1), ey:+Y(e.lat).toFixed(1) };
}

// ── Fuel used since the last fuel-up (most recent fill on/before this date) ──
function fuelSinceFill(entry, fuelLog) {
  if (!Array.isArray(fuelLog) || !fuelLog.length || entry.engine_end == null || entry.engine_end === '') return null;
  const fills = fuelLog
    .filter(f => f.engine_hrs != null && f.date && f.date <= entry.date)
    .sort((a,b) => new Date(b.date) - new Date(a.date));
  const last = fills[0];
  if (!last) return null;
  const hrs = Number(entry.engine_end) - Number(last.engine_hrs);
  if (!(hrs > 0)) return null;
  const rate = Number(last.gal_per_hr) || 1.0;
  return { date:last.date, hrs:Math.round(hrs*10)/10, gal:Math.round(hrs*rate*10)/10, est:!last.gal_per_hr };
}


// ── Load Leaflet from CDN once (no bundled dependency) ──
function loadLeaflet() {
  if (typeof window === 'undefined') return Promise.reject();
  if (window.L) return Promise.resolve(window.L);
  if (window._pmfLeaflet) return window._pmfLeaflet;
  window._pmfLeaflet = new Promise((resolve, reject) => {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(css);
    const s = document.createElement('script');
    s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    s.async = true;
    s.onload = () => resolve(window.L);
    s.onerror = () => reject(new Error('leaflet failed'));
    document.head.appendChild(s);
  });
  return window._pmfLeaflet;
}

// ── Route map: Leaflet (CARTO/OSM) over an SVG-route fallback ──
const BOAT_SVG = '<div class="pmf-boat"><svg width="34" height="34" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.45))"><path d="M3.5 15 Q12 21.5 20.5 15 Z" fill="#52656D"/><line x1="12" y1="3.5" x2="12" y2="15" stroke="#52656D" stroke-width="1.2"/><path d="M12 4.5 Q18 10 16.5 14.2 L12 14.2 Z" fill="#C0392B"/><path d="M12 4.5 Q16.2 8.7 15.4 12.6 L12 13 Z" fill="#fff"/><path d="M12 4.5 Q14.6 7.2 14.1 10.4 L12 10.8 Z" fill="#2b4a8f"/></svg></div>';

// Require two fingers to pan the map, so a single-finger touch scrolls the page
// (and doesn't accidentally drag the map when reading a post). Shows a hint on
// a one-finger drag attempt.
function enableTwoFingerPan(map, container) {
  if (!container) return;
  let hint = container.querySelector('.map-2f-hint');
  if (!hint) {
    hint = document.createElement('div');
    hint.className = 'map-2f-hint';
    hint.textContent = 'Use two fingers to move the map';
    Object.assign(hint.style, {
      position:'absolute', inset:'0', display:'flex', alignItems:'center', justifyContent:'center',
      background:'rgba(11,22,38,.55)', color:'#fff', font:"600 13px 'Plus Jakarta Sans',sans-serif",
      textAlign:'center', padding:'0 24px', opacity:'0', transition:'opacity .15s', pointerEvents:'none',
      zIndex:'500', borderRadius:'inherit',
    });
    container.appendChild(hint);
  }
  let hideT;
  const onTouchStart = (e) => {
    if (e.touches.length === 1) {
      map.dragging.disable();
      hint.style.opacity = '1';
      clearTimeout(hideT); hideT = setTimeout(() => { hint.style.opacity = '0'; }, 1000);
    } else if (e.touches.length >= 2) {
      map.dragging.enable();
      hint.style.opacity = '0';
    }
  };
  const onTouchEnd = (e) => { if (e.touches.length === 0) { hint.style.opacity = '0'; } };
  container.addEventListener('touchstart', onTouchStart, { passive:true });
  container.addEventListener('touchend', onTouchEnd, { passive:true });
  // start disabled on touch devices; mouse drag still works
  if ('ontouchstart' in window) map.dragging.disable();
}

function RouteMap({ track, nm, seamarks = true, inCarousel = false }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const sailRef = useRef(null);
  const rafRef = useRef(null);
  const [pct, setPct] = useState(0);
  const rp = trackPath(track, 520, 520, 26);

  useEffect(() => {
    let cancelled = false;
    if (!ref.current || !Array.isArray(track) || track.length < 2) return;
    loadLeaflet().then(L => {
      if (cancelled || !ref.current || mapRef.current) return;
      const pts = track.map(p => [p.lat, p.lon]);
      const map = L.map(ref.current, { zoomControl:true, attributionControl:true, scrollWheelZoom:false, dragging:true, tap:false });
      enableTwoFingerPan(map, ref.current);
      mapRef.current = map;
      map.getContainer().style.background = 'transparent';
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { maxZoom:19, attribution:'\u00A9 OpenStreetMap \u00A9 CARTO' }).addTo(map);
      if (seamarks) L.tileLayer('https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png', { maxZoom:18, attribution:'\u00A9 OpenSeaMap' }).addTo(map);
      L.polyline(pts, { color:'#6E8799', weight:5, opacity:.18, lineJoin:'round', lineCap:'round' }).addTo(map);
      L.polyline(pts, { color:'#6E8799', weight:1.8, opacity:.55, dashArray:'2 5' }).addTo(map);
      const sailed = L.polyline([pts[0]], { color:'#B8860B', weight:3.4, opacity:.95, lineJoin:'round', lineCap:'round' }).addTo(map);
      L.circleMarker(pts[0], { radius:6, color:'#fff', weight:2, fillColor:'#B8860B', fillOpacity:1 }).addTo(map).bindTooltip('Departure', { direction:'top' });
      L.circleMarker(pts[pts.length-1], { radius:6, color:'#fff', weight:2, fillColor:'#C0392B', fillOpacity:1 }).addTo(map).bindTooltip('Arrival', { direction:'top' });
      const icon = L.divIcon({ html: BOAT_SVG, className:'', iconSize:[34,34], iconAnchor:[17,17] });
      const boat = L.marker(pts[0], { icon, zIndexOffset:1000 }).addTo(map);
      map.fitBounds(L.polyline(pts).getBounds(), { padding:[34,34] });
      setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 160);

      sailRef.current = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        const seg = pts.length - 1, t0 = performance.now(), D = 9000;
        const frame = (now) => {
          if (!mapRef.current) return;
          const p = Math.min(1, (now - t0) / D);
          const f = p * seg, i = Math.min(seg - 1, Math.floor(f)), r = f - i;
          const a = pts[i], b = pts[i + 1];
          const lat = a[0] + (b[0] - a[0]) * r, lon = a[1] + (b[1] - a[1]) * r;
          boat.setLatLng([lat, lon]);
          const el = boat.getElement() && boat.getElement().querySelector('.pmf-boat');
          if (el) el.style.transform = (b[1] < a[1]) ? 'scaleX(-1)' : 'scaleX(1)';
          sailed.setLatLngs(pts.slice(0, i + 1).concat([[lat, lon]]));
          setPct(Math.round(p * 100));
          if (p < 1) rafRef.current = requestAnimationFrame(frame);
        };
        rafRef.current = requestAnimationFrame(frame);
      };
      setTimeout(() => { if (sailRef.current) sailRef.current(); }, 700);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [track, seamarks]);

  return (
    <div style={ inCarousel
      ? { position:'absolute', inset:0, background:'var(--s2)', overflow:'hidden' }
      : { position:'relative', width:'100%', aspectRatio:'1 / 1', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', background:'var(--s2)', overflow:'hidden' } }>
      {rp && (
        <svg viewBox="0 0 520 520" xmlns="http://www.w3.org/2000/svg" style={{ position:'absolute', inset:0, width:'100%', height:'100%', zIndex:0 }}>
          <path d={rp.d} fill="none" stroke="#6E8799" strokeWidth="2.4" strokeOpacity="0.95" strokeLinejoin="round" strokeLinecap="round"/>
          <circle cx={rp.sx} cy={rp.sy} r="5" fill="#B8860B"/>
          <circle cx={rp.ex} cy={rp.ey} r="5" fill="#C0392B"/>
        </svg>
      )}
      <div ref={ref} style={{ position:'absolute', inset:0, zIndex:1 }}/>
      {nm != null && <span style={{ position:'absolute', top:12, right:12, zIndex:600, ...T.meta, color:'var(--gold-deep, #8B6508)', background:'rgba(255,255,255,.92)', border:'1px solid var(--border)', padding:'4px 9px', borderRadius:6 }}>{nm} nm</span>}
      <button onClick={()=>sailRef.current && sailRef.current()} style={{ position:'absolute', left:12, bottom: inCarousel ? 44 : 12, zIndex:600, background:'rgba(11,22,38,.9)', color:'var(--gold-lt)', border:'none', borderRadius:8, padding:'8px 12px', ...T.meta, letterSpacing:'.06em', cursor:'pointer' }}>▶ Replay</button>
      <span style={{ position:'absolute', right:12, bottom: inCarousel ? 44 : 12, zIndex:600, background:'rgba(255,255,255,.92)', border:'1px solid var(--border)', borderRadius:8, padding:'6px 10px', fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, color:'var(--navy)' }}>{pct >= 100 ? 'Arrived' : `${pct}%`}</span>
    </div>
  );
}

// ── Whole-voyage map: all legs stitched, animated like a single card ──
function VoyageMap({ entries }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const sailRef = useRef(null);
  const rafRef = useRef(null);
  const [pct, setPct] = useState(0);
  const { track, stops } = useMemo(() => voyageTrack(entries), [entries]);
  const hasTrack = Array.isArray(track) && track.length > 1;

  useEffect(() => {
    let cancelled = false;
    if (!ref.current || !hasTrack) return;
    loadLeaflet().then(L => {
      if (cancelled || !ref.current || mapRef.current) return;
      const pts = track.map(p => [p.lat, p.lon]);
      const map = L.map(ref.current, { zoomControl:true, attributionControl:true, scrollWheelZoom:false, dragging:true, tap:false });
      enableTwoFingerPan(map, ref.current);
      mapRef.current = map;
      map.getContainer().style.background = 'transparent';
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { maxZoom:19, attribution:'\u00A9 OpenStreetMap \u00A9 CARTO' }).addTo(map);
      L.tileLayer('https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png', { maxZoom:18, attribution:'\u00A9 OpenSeaMap' }).addTo(map);
      L.polyline(pts, { color:'#6E8799', weight:5, opacity:.18, lineJoin:'round', lineCap:'round' }).addTo(map);
      L.polyline(pts, { color:'#6E8799', weight:1.8, opacity:.55, dashArray:'2 5' }).addTo(map);
      const sailed = L.polyline([pts[0]], { color:'#B8860B', weight:3.4, opacity:.95, lineJoin:'round', lineCap:'round' }).addTo(map);
      // A dot at each leg stop (gold=anchor/dock stay, navy=passage waypoint)
      stops.forEach((s, i) => {
        L.circleMarker([s.lat, s.lon], { radius:5, color:'#fff', weight:2, fillColor: s.stay ? '#B8860B' : '#6E8799', fillOpacity:1 })
          .addTo(map).bindTooltip(s.label || (s.stay ? 'Stop' : 'Waypoint'), { direction:'top' });
      });
      L.circleMarker(pts[0], { radius:6, color:'#fff', weight:2, fillColor:'#B8860B', fillOpacity:1 }).addTo(map).bindTooltip('Start', { direction:'top' });
      L.circleMarker(pts[pts.length-1], { radius:6, color:'#fff', weight:2, fillColor:'#C0392B', fillOpacity:1 }).addTo(map).bindTooltip('Latest', { direction:'top' });
      const icon = L.divIcon({ html: BOAT_SVG, className:'', iconSize:[34,34], iconAnchor:[17,17] });
      const boat = L.marker(pts[0], { icon, zIndexOffset:1000 }).addTo(map);
      map.fitBounds(L.polyline(pts).getBounds(), { padding:[34,34] });
      setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 160);

      sailRef.current = () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        const seg = pts.length - 1, t0 = performance.now(), D = Math.min(16000, 4000 + seg * 120);
        const frame = (now) => {
          if (!mapRef.current) return;
          const p = Math.min(1, (now - t0) / D);
          const f = p * seg, i = Math.min(seg - 1, Math.floor(f)), r = f - i;
          const a = pts[i], b = pts[i + 1];
          const lat = a[0] + (b[0] - a[0]) * r, lon = a[1] + (b[1] - a[1]) * r;
          boat.setLatLng([lat, lon]);
          const el = boat.getElement() && boat.getElement().querySelector('.pmf-boat');
          if (el) el.style.transform = (b[1] < a[1]) ? 'scaleX(-1)' : 'scaleX(1)';
          sailed.setLatLngs(pts.slice(0, i + 1).concat([[lat, lon]]));
          setPct(Math.round(p * 100));
          if (p < 1) rafRef.current = requestAnimationFrame(frame);
        };
        rafRef.current = requestAnimationFrame(frame);
      };
      setTimeout(() => { if (sailRef.current) sailRef.current(); }, 700);
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [track, stops, hasTrack]);

  if (!hasTrack) return null;

  return (
    <div style={{ position:'relative', width:'100%', aspectRatio:'1 / 1', borderRadius:16, overflow:'hidden', background:'var(--s2)', marginBottom:16, boxShadow:'0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)' }}>
      <div ref={ref} style={{ position:'absolute', inset:0, zIndex:1 }}/>
      <button onClick={()=>sailRef.current && sailRef.current()} style={{ position:'absolute', left:12, bottom:12, zIndex:600, background:'rgba(11,22,38,.9)', color:'var(--gold-lt)', border:'none', borderRadius:8, padding:'8px 12px', ...T.meta, letterSpacing:'.06em', cursor:'pointer' }}>▶ Replay</button>
      <span style={{ position:'absolute', right:12, bottom:12, zIndex:600, background:'rgba(255,255,255,.92)', border:'1px solid var(--border)', borderRadius:8, padding:'6px 10px', fontFamily:"'IBM Plex Mono',monospace", fontSize:10, fontWeight:700, color:'var(--navy)' }}>{pct >= 100 ? 'Complete' : `${pct}%`}</span>
    </div>
  );
}

// ── Swipeable media: route map first, then photos ──
function MediaCarousel({ track, nm, photos = [], coverUrl, wide = false }) {
  const railRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const hasTrack = Array.isArray(track) && track.length > 1;

  // Cover photo leads the photo slides, the rest follow in order.
  const pics = (Array.isArray(photos) ? photos : []).slice().sort((a, b) => (b.url === coverUrl) - (a.url === coverUrl));
  const slides = (hasTrack ? 1 : 0) + pics.length;
  if (slides === 0) return null;

  const onScroll = () => {
    const r = railRef.current;
    if (!r) return;
    setIdx(Math.round(r.scrollLeft / r.clientWidth));
  };
  const go = (d) => {
    const r = railRef.current;
    if (r) r.scrollBy({ left: d * r.clientWidth, behavior: 'smooth' });
  };

  const arrow = (side) => ({
    position:'absolute', top:'50%', transform:'translateY(-50%)', [side]:10, zIndex:700,
    width:34, height:34, borderRadius:'50%', background:'rgba(255,255,255,.92)', border:'1px solid var(--border)',
    display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--navy)', fontSize:17,
  });

  return (
    <div style={{ position:'relative', width:'100%', aspectRatio: wide ? '4 / 3' : '1 / 1', borderTop:'1px solid var(--border)', borderBottom:'1px solid var(--border)', background:'var(--s2)', overflow:'hidden' }}>
      <div ref={railRef} onScroll={onScroll} style={{ display:'flex', height:'100%', width:'100%', overflowX:'auto', scrollSnapType:'x mandatory', WebkitOverflowScrolling:'touch', scrollbarWidth:'none' }}>
        {hasTrack && (
          <div style={{ flex:'0 0 100%', height:'100%', scrollSnapAlign:'center', position:'relative' }}>
            <RouteMap track={track} nm={nm} inCarousel/>
          </div>
        )}
        {pics.map((p, i) => (
          <div key={p.url || i} style={{ flex:'0 0 100%', height:'100%', scrollSnapAlign:'center', position:'relative' }}>
            <img src={p.url} alt="" loading="lazy" decoding="async" style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }}/>
            {p.url === coverUrl && <span style={{ position:'absolute', left:14, bottom:44, ...T.meta, color:'#fff', textShadow:'0 1px 4px rgba(0,0,0,.6)' }}>Cover</span>}
          </div>
        ))}
      </div>

      {slides > 1 && (
        <>
          {idx > 0 && <div onClick={()=>go(-1)} style={arrow('left')}>‹</div>}
          {idx < slides-1 && <div onClick={()=>go(1)} style={arrow('right')}>›</div>}
          <div style={{ position:'absolute', left:0, right:0, bottom:12, zIndex:700, display:'flex', justifyContent:'center', gap:7, pointerEvents:'none' }}>
            {Array.from({ length: slides }).map((_, i) => {
              const isMap = hasTrack && i === 0, on = i === idx;
              return <span key={i} style={{
                width: on ? 20 : 7, height:7, borderRadius: on ? 4 : '50%',
                background: isMap ? (on ? 'var(--gold)' : 'transparent') : (on ? 'var(--navy)' : 'rgba(11,22,38,.25)'),
                border: isMap ? '1.5px solid var(--gold)' : 'none', transition:'all .2s',
              }}/>;
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Shareable passage report card ──

// "Since PMF was last underway" — top-of-feed banner. Live-updates the hours.
function SinceBanner({ entries }) {
  const lu = useMemo(() => lastUnderway(entries), [entries]);
  const outToday = useMemo(() => isOutToday(entries), [entries]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000); // refresh each minute
    return () => clearInterval(id);
  }, []);
  if (!lu) return null;

  if (outToday) {
    return (
      <div style={{ background:'linear-gradient(135deg,#6A7D85,#52656D)', borderRadius:16, padding:'16px 18px', margin:'0 4px 16px', color:'#fff', display:'flex', alignItems:'center', gap:14 }}>
        <span style={{ flex:'none', width:38, height:38, borderRadius:10, background:'rgba(255,255,255,.1)', display:'flex', alignItems:'center', justifyContent:'center', color:'#7FD48C' }}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 17l6-3 6 3 6-3V6l-6 3-6-3-6 3z"/></svg>
        </span>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:SANS, fontSize:12, fontWeight:500, color:'rgba(255,255,255,.7)' }}>Out today</div>
          <div style={{ fontFamily:MONO, fontSize:22, fontWeight:600, marginTop:2, letterSpacing:'-.01em' }}>On the water</div>
          {lu.entry.title && <div style={{ fontFamily:MONO, fontSize:10, color:'rgba(255,255,255,.55)', marginTop:3 }}>{lu.entry.title}</div>}
        </div>
      </div>
    );
  }

  const p = sinceParts(now - lu.ts);
  const last = lu.entry;
  return (
    <div style={{ background:'var(--navy)', borderRadius:16, padding:'16px 18px', margin:'0 4px 16px', color:'#fff', display:'flex', alignItems:'center', gap:14 }}>
      <span style={{ flex:'none', width:38, height:38, borderRadius:10, background:'rgba(255,255,255,.1)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--gold-lt)' }}>
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
      </span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontFamily:SANS, fontSize:12, fontWeight:500, color:'rgba(255,255,255,.7)' }}>Since PMF was last underway</div>
        <div style={{ fontFamily:MONO, fontSize:22, fontWeight:600, marginTop:2, letterSpacing:'-.01em' }}>
          {p ? <>{p.a} <span style={{ color:'var(--gold-lt)' }}>{p.aU}</span>{p.b != null ? <> {p.b} <span style={{ color:'var(--gold-lt)' }}>{p.bU}</span></> : null}</> : '—'}
        </div>
        <div style={{ fontFamily:MONO, fontSize:10, color:'rgba(255,255,255,.55)', marginTop:3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          Last out {(fmtDate(last.date)||'').replace(/^\w+,\s*/,'').replace(/,\s*\d{4}$/,'')}{last.title ? ` · ${last.title}` : ''}
        </div>
      </div>
    </div>
  );
}

function StatsStrip({ entries, settings = {} }) {
  // Sea time is elapsed hours per passage. (This used to add time_at_sea AND
  // moving_hrs together, which double-counted every trip.)
  const hrs = entries.reduce((sum, e) => sum + (totalHrs(e) || 0), 0);
  const lastEng = engineHours(entries, settings);
  const totalNm = hullNm(entries, settings);
  const cells = [
    ['Passages', entries.length],
    ['Sea time', fmtHrs(hrs) || '0h'],
    ['Hull nm', totalNm > 0 ? totalNm.toLocaleString() : '—'],
    ['Engine h', lastEng ? Number(lastEng).toLocaleString() : '—'],
  ];
  return (
    <div style={{ display:'flex', background:'var(--surface)', borderRadius:16, padding:'18px 4px', boxShadow:'0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)', marginBottom:20 }}>
      {cells.map(([l,v], i) => (
        <div key={l} style={{ flex:1, textAlign:'center', position:'relative', borderLeft: i ? '1px solid var(--hair, #E7E1D5)' : 'none' }}>
          <div style={{ fontFamily:MONO, fontSize:20, fontWeight:600, color:'var(--navy)', letterSpacing:'-.01em' }}>{v}</div>
          <div style={{ fontFamily:SANS, fontSize:9.5, fontWeight:600, letterSpacing:'.09em', textTransform:'uppercase', color:'var(--slate)', marginTop:5 }}>{l}</div>
        </div>
      ))}
    </div>
  );
}

export default function LogbookTab({ entries, onEdit, onDelete, fuelLog = [], settings = {}, voyages = [], onNewVoyage, onEditVoyage, onDeleteVoyage, allBoats = false, boatsById = {} }) {
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState('feed');          // 'feed' | 'voyages'
  const [openVoyageId, setOpenVoyageId] = useState(null);
  const mobile = useIsMobile();

  const filtered = useMemo(() => [...entries]
    .sort((a,b) => timelineTs(b) - timelineTs(a))
    .filter(e => {
      const q = search.toLowerCase();
      const matchQ = !q || [e.departure,e.destination,e.crew,e.comments,e.date,e.title].some(f=>(f||'').toLowerCase().includes(q));
      const matchF = filter==='all'
        || (filter==='race'    && isRaceEntry(e))
        || (filter==='solo'    && e.solo==='Y')
        || (filter==='anchor'  && isStayEntry(e))
        || (filter==='passage' && e.trip_type==='Passage');
      return matchQ && matchF;
    }), [entries, search, filter]);

  const selected = entries.find(e=>e.id===selectedId) || (!mobile && view==='feed' ? filtered[0] : null);

  const pill = (id,lbl) => (
    <button key={id} onClick={()=>setFilter(id)} style={{
      fontFamily:SANS, fontSize:12, fontWeight:600,
      padding:'8px 0', marginRight:20, background:'none', border:'none', cursor:'pointer',
      color: filter===id ? 'var(--navy)' : 'var(--slate)',
      borderBottom: filter===id ? '2px solid var(--gold)' : '2px solid transparent',
      marginBottom:-1,
    }}>{lbl}</button>
  );

  const controls = (
    <>
      <div style={{ display:'flex', alignItems:'center', gap:8, margin:'0 4px 12px', borderBottom:'1px solid var(--hair, #E7E1D5)' }}>
        <div style={{ display:'flex', flexWrap:'nowrap', overflowX:'auto', flex:1, minWidth:0 }}>
          {[['all','All'],['race','Races'],['anchor','Aboard'],['solo','Solo'],['passage','Passages']].map(([id,lbl])=>pill(id,lbl))}
        </div>
        <button onClick={()=>setSearchOpen(o=>!o)} aria-label="Search" style={{ flex:'0 0 auto', width:34, height:34, borderRadius:9, border:'none', background: searchOpen ? 'var(--navy)' : 'var(--tint, #FAF8F4)', color: searchOpen ? '#fff' : 'var(--slate)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:6 }}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>
        </button>
      </div>
      {searchOpen && (
        <input type="text" autoFocus placeholder="Search port, crew, notes" value={search} onChange={e=>setSearch(e.target.value)} style={{ marginBottom:12, border:'none', borderRadius:12, boxShadow:'0 1px 2px rgba(11,22,38,.04)' }}/>
      )}
    </>
  );

  const feed = filtered.length === 0
    ? <div style={{ textAlign:'center', padding:'48px 20px', color:'var(--slate)', fontSize:14 }}>{search||filter!=='all'?'No entries match.':'No log entries yet. Tap + to add one.'}</div>
    : (
      <div style={{ background:'var(--surface)', borderRadius:16, overflow:'hidden', boxShadow:'0 1px 2px rgba(11,22,38,.04), 0 8px 24px rgba(11,22,38,.06)' }}>
        {filtered.map((e,i) => (
          <div key={e.id} style={ i === 0 ? { borderTop:'none' } : undefined }>
            <SummaryCard entry={e} active={!mobile && selected?.id===e.id} onOpen={ent=>setSelectedId(ent.id)} first={i===0} compact={!mobile} allBoats={allBoats} boatsById={boatsById}/>
          </div>
        ))}
      </div>
    );

  // ── Feed ⇄ Voyages toggle (top of the Logbook tab) ──
  const toggle = (
    <div style={{ display:'flex', background:'var(--s2)', borderRadius:12, padding:4, gap:4, margin:'0 4px 16px' }}>
      {[['feed','Feed', <path key="f" d="M4 5h16M4 12h16M4 19h10"/>], ['voyages','Voyages', <path key="v" d="M3 17l6-3 6 3 6-3V6l-6 3-6-3-6 3z"/>]].map(([id,lbl,icon]) => (
        <button key={id} onClick={()=>{ setView(id); setOpenVoyageId(null); }} style={{
          flex:1, background: view===id ? 'var(--surface)' : 'none', border:'none', borderRadius:9,
          padding:'11px 4px', fontFamily:SANS, fontSize:13, fontWeight:600,
          color: view===id ? 'var(--navy)' : 'var(--slate)', cursor:'pointer',
          boxShadow: view===id ? '0 1px 3px rgba(11,22,38,.1)' : 'none',
          display:'flex', alignItems:'center', justifyContent:'center', gap:7,
        }}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{icon}</svg>
          {lbl}
        </button>
      ))}
    </div>
  );

  // Entries grouped by voyage, for the list cards.
  const entriesByVoyage = useMemo(() => {
    const map = {};
    (voyages || []).forEach(v => { map[v.id] = []; });
    entries.forEach(e => { if (e.voyage_id && map[e.voyage_id]) map[e.voyage_id].push(e); });
    return map;
  }, [entries, voyages]);

  const openVoyage = voyages.find(v => v.id === openVoyageId) || null;

  // A tapped leg opens that entry's normal detail view.
  const voyageContent = openVoyage
    ? <VoyagePage
        voyage={openVoyage}
        entries={entriesByVoyage[openVoyage.id] || []}
        DetailCard={({ entry, first, onOpen }) => <SummaryCard entry={entry} onOpen={onOpen} first={first}/>}
        MapBlock={<VoyageMap entries={entriesByVoyage[openVoyage.id] || []}/>}
        onBack={()=>setOpenVoyageId(null)}
        onEdit={onEditVoyage}
        onDelete={onDeleteVoyage}
        onOpenLeg={(e)=>setSelectedId(e.id)}
      />
    : <VoyageList voyages={voyages} entriesByVoyage={entriesByVoyage} onOpen={(v)=>setOpenVoyageId(v.id)} onNew={onNewVoyage}/>;

  // ── MOBILE: feed → tap → full-screen detail ──
  if (mobile) {
    if (selected) {
      // Swipe right → back to feed; swipe left → next post (in feed order).
      const order = filtered.length ? filtered : entries;
      const idx = order.findIndex(e => e.id === selected.id);
      const goNext = () => { if (idx >= 0 && idx < order.length - 1) setSelectedId(order[idx + 1].id); };
      let sx = 0, sy = 0, sw = false;
      const onTS = (e) => { if (e.touches.length === 1) { sx = e.touches[0].clientX; sy = e.touches[0].clientY; sw = true; } else { sw = false; } };
      const onTE = (e) => {
        if (!sw) return; sw = false;
        const t = e.changedTouches[0]; const dx = t.clientX - sx; const dy = t.clientY - sy;
        if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return; // must be a mostly-horizontal swipe
        if (dx < 0) { setSelectedId(null); }   // swipe left → back to feed
        else { goNext(); }                       // swipe right → next post
      };
      return (
        <>
          <div onTouchStart={onTS} onTouchEnd={onTE} style={{ position:'fixed', inset:0, zIndex:150, background:'var(--bg)', overflowY:'auto', paddingBottom:'calc(96px + env(safe-area-inset-bottom))' }}>
            <DetailView entry={selected} entries={entries} fuelLog={fuelLog} onEdit={onEdit} onDelete={onDelete} onBack={()=>setSelectedId(null)}/>
          </div>
        </>
      );
    }
    return (
      <div style={{ paddingBottom:'calc(88px + env(safe-area-inset-bottom))' }}>
        {toggle}
        {view === 'voyages'
          ? voyageContent
          : <><SinceBanner entries={entries}/><StatsStrip entries={entries} settings={settings}/>{controls}{feed}</>}
      </div>
    );
  }

  // ── DESKTOP: master list + detail pane ──
  if (view === 'voyages') {
    return (
      <div>
        {toggle}
        {voyageContent}
        {selected && (
          <div onClick={()=>setSelectedId(null)} style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(11,22,38,.45)', display:'flex', justifyContent:'center', alignItems:'flex-start', padding:'40px 20px', overflowY:'auto' }}>
            <div onClick={e=>e.stopPropagation()} style={{ width:'100%', maxWidth:720, background:'var(--surface)', borderRadius:16, overflow:'hidden', boxShadow:'0 20px 50px rgba(11,22,38,.3)' }}>
              <DetailView entry={selected} entries={entries} fuelLog={fuelLog} onEdit={onEdit} onDelete={onDelete} onBack={()=>setSelectedId(null)}/>
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div>
      {toggle}
      <SinceBanner entries={entries}/>
      <StatsStrip entries={entries} settings={settings}/>
      {controls}
      <div style={{ display:'grid', gridTemplateColumns:'320px minmax(0, 1fr)', gap:24, alignItems:'start' }}>
        <div>{feed}</div>
        <div style={{ position:'sticky', top:16, maxHeight:'calc(100vh - 32px)', overflowY:'auto', overflowX:'hidden', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, boxShadow:'var(--sh)', minHeight:400 }}>
          <DetailView entry={selected} entries={entries} fuelLog={fuelLog} onEdit={onEdit} onDelete={onDelete} onBack={null} wide/>
        </div>
      </div>
    </div>
  );
}
